import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "src/config/apiBase";
import { decryptToken } from "../utils/getToken";
import { toast } from "react-toastify";
import { Sparkles, Copy, MessageCircle, Mail } from "lucide-react";
import { TIER_STYLES } from "../../utils/leadScore";
import CrmPlaybookPanel from "./CrmPlaybookPanel";

const CrmLeadIntelligence = ({ leadId, mobile, email }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draftLang, setDraftLang] = useState("english");
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    setLoading(true);
    fetch(`${url}/leads/intelligence/${leadId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, url, token]);

  const copyDraft = (text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    toast.info("Copied to clipboard");
  };

  const openWhatsApp = (text) => {
    if (!mobile || !text) return;
    const num = String(mobile).replace(/\D/g, "");
    const waNum = num.startsWith("91") ? num : `91${num}`;
    window.open(`https://wa.me/${waNum}?text=${encodeURIComponent(text)}`, "_blank");
  };

  const openEmail = () => {
    const subj = data?.drafts?.email?.subject;
    const body = data?.drafts?.email?.body;
    if (!email || !subj) return;
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(body || "")}`;
  };

  if (!leadId) return null;
  if (loading) return <p className="crm-insights__muted">Loading AI insights…</p>;
  if (!data) return null;

  const tierStyle = TIER_STYLES[data.predictive?.tier] || TIER_STYLES.Cold;
  const draft =
    draftLang === "hindi"
      ? data.drafts?.hindi
      : draftLang === "whatsapp"
        ? data.drafts?.whatsapp
        : draftLang === "email"
          ? data.drafts?.email?.body
          : data.drafts?.english;

  return (
    <div className="crm-intelligence">
      <div className="crm-intelligence__head">
        <Sparkles size={18} />
        <strong>Lead intelligence</strong>
        <span className={`crm-board__tier border ${tierStyle.className}`}>
          {data.predictive?.tier} · {data.predictive?.probability}%
        </span>
      </div>

      <p className="crm-intelligence__summary">{data.summary}</p>

      <div className="crm-intelligence__nba">
        <span>Next best action</span>
        <strong>{data.nextBestAction}</strong>
      </div>

      {data.predictedCloseDate && (
        <p className="crm-intelligence-compact__close">
          Est. close: {data.predictedCloseDate}
          {data.daysToClose ? ` (~${data.daysToClose} days)` : ""}
        </p>
      )}

      {data.predictive?.reasons?.length > 0 && (
        <ul className="crm-intelligence__reasons">
          {data.predictive.reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}

      <div className="crm-intelligence__drafts">
        <div className="crm-intelligence__draft-head">
          <span>Follow-up draft</span>
          <select
            value={draftLang}
            onChange={(e) => setDraftLang(e.target.value)}
            className="crm-insights__select"
          >
            <option value="english">English</option>
            <option value="hindi">Hindi</option>
            <option value="whatsapp">WhatsApp short</option>
            <option value="email">Email</option>
          </select>
        </div>
        {draftLang === "email" && data.drafts?.email?.subject && (
          <p className="crm-intelligence__email-subject">
            Subject: {data.drafts.email.subject}
          </p>
        )}
        <textarea readOnly value={draft || ""} rows={5} className="crm-intelligence__draft-box" />
        <div className="crm-intelligence__draft-actions">
          <button type="button" onClick={() => copyDraft(draft)}>
            <Copy size={14} /> Copy
          </button>
          {mobile && draftLang !== "email" && (
            <button type="button" onClick={() => openWhatsApp(draft)}>
              <MessageCircle size={14} /> WhatsApp
            </button>
          )}
          {email && draftLang === "email" && (
            <button type="button" onClick={openEmail}>
              <Mail size={14} /> Send email
            </button>
          )}
        </div>
      </div>

      {data.playbook?.length > 0 && (
        <CrmPlaybookPanel leadId={leadId} />
      )}
    </div>
  );
};

export default CrmLeadIntelligence;
